/**
 * Segment palettes for the wheel.
 *
 * Applied to any entry without a colour of its own, cycled by position — which
 * is what guarantees adjacent segments never match. Every palette is ordered so
 * that neighbours contrast: sorting one by hue would put four near-identical
 * blues side by side and make the wheel unreadable.
 *
 * Sixteen colours each, because the palette is indexed with `i % length` and a
 * shorter list starts repeating on a wheel of 17 names.
 */

export const PALETTES = {
  /** The original set. High-saturation and high-contrast — good on a projector. */
  classic: [
    '#EE4040', '#F0A30A', '#7DBD43', '#3BAED0',
    '#9B59B6', '#E67E22', '#27AE60', '#E91E8C',
    '#2980B9', '#D35400', '#16A085', '#8E44AD',
    '#C0392B', '#F39C12', '#1ABC9C', '#2C3E50',
  ],
  /** Muted and desaturated — reads as considered rather than carnival. */
  pastel: [
    '#F4A9A8', '#F6C89F', '#C9DFA0', '#A8D8E8',
    '#C9B6E4', '#F3B9A0', '#A9D8B8', '#F2AECD',
    '#A7C4E0', '#E8BFA0', '#A5D6C7', '#C4AEDD',
    '#EDA9A2', '#F2D3A0', '#AEDCD2', '#B9C4D4',
  ],
  /** Corporate blues and teals for a company draw or a trade-show screen. */
  corporate: [
    '#1F3A5F', '#2E6DA4', '#3E8FB0', '#4FB0AA',
    '#2C4A6E', '#3D7EB8', '#4A9CC0', '#5CBFB6',
    '#25436B', '#3576AE', '#449BB8', '#55B7B0',
    '#1A3454', '#2A6299', '#3A8CA8', '#48A79E',
  ],
  /** Warm end of the spectrum only — reds through yellows. */
  sunset: [
    '#C0392B', '#E74C3C', '#E67E22', '#F39C12',
    '#D35400', '#FF6B4A', '#F0932B', '#FFB142',
    '#A93226', '#EC7063', '#DC7633', '#F5B041',
    '#922B21', '#E59866', '#CA6F1E', '#F8C471',
  ],
  /** Greens and blues — calm, and the easiest set on a long look. */
  forest: [
    '#1E5631', '#2E7D32', '#4CAF50', '#7CB342',
    '#00695C', '#00897B', '#26A69A', '#66BB6A',
    '#33691E', '#558B2F', '#8BC34A', '#43A047',
    '#004D40', '#00796B', '#009688', '#689F38',
  ],
  /** Near-monochrome. For a wheel where the labels should carry everything. */
  slate: [
    '#2C3E50', '#465C71', '#5D7185', '#78909C',
    '#37474F', '#546E7A', '#6B8494', '#90A4AE',
    '#22313F', '#3E5163', '#68798A', '#8496A3',
    '#1B2733', '#4A5F72', '#728496', '#9AA9B4',
  ],
} as const;

export type PaletteName = keyof typeof PALETTES;

export const PALETTE_NAMES = Object.keys(PALETTES) as PaletteName[];

/** Runtime guard for a value coming out of localStorage or a share link. */
export function isPaletteName(v: unknown): v is PaletteName {
  return typeof v === 'string' && v in PALETTES;
}
